import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import GlassSheet from "@/components/v2/GlassSheet";
import { api } from "@/hooks/useAuth";

export default function BlessingSheet({ open, onClose, user, onOpened }) {
  const [available, setAvailable] = useState([]);
  const [recent, setRecent] = useState([]);
  const [revealed, setRevealed] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/blessings"); setAvailable(data.available || []); setRecent(data.recent || []); } catch (e) {}
  };
  useEffect(() => { if (open && user) load(); }, [open, user]);

  const reveal = async (b) => {
    try {
      const { data } = await api.post(`/blessings/${b.id}/open`);
      setRevealed(data);
      onOpened?.();
      await load();
    } catch (e) { toast.error("Could not open blessing."); }
  };

  return (
    <GlassSheet open={open} onClose={onClose} eyebrow="A Blessing from KALKI" title={revealed ? "A gentle word." : (available.length ? "A blessing awaits you." : "No blessing yet.")} testid="blessing-sheet">
      <AnimatePresence mode="wait">
        {revealed ? (
          <motion.div key="revealed" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-5">
            <motion.div
              aria-hidden
              initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: [0, 0.9, 0.6], scale: [0.6, 1.1, 1] }}
              transition={{ duration: 1.4, ease: "easeOut" }}
              className="mx-auto w-24 h-24 rounded-full"
              style={{ background: "radial-gradient(circle, rgba(240,209,138,0.65) 0%, rgba(124,58,237,0.35) 45%, transparent 70%)", filter: "blur(6px)" }}
            />
            <p data-testid="blessing-message" className="font-serif text-[20px] sm:text-[22px] leading-relaxed text-slate-100 italic text-center">
              “{revealed.message}”
            </p>
            <p className="text-center text-[11px] tracking-[0.3em] uppercase text-amber-300/80">— KALKI</p>
            <button onClick={() => { setRevealed(null); }} className="w-full rounded-full py-3 text-[13px] text-slate-200 border border-white/10 bg-white/5">Close</button>
          </motion.div>
        ) : available.length > 0 ? (
          <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
            <p className="text-slate-300 text-[14px]">Something you did has stayed with me. Tap to receive.</p>
            {available.map((b) => (
              <button key={b.id} data-testid={`blessing-open-${b.id}`} onClick={() => reveal(b)}
                className="w-full rounded-2xl py-5 text-slate-50 font-medium backdrop-blur-xl"
                style={{ background: "linear-gradient(140deg, rgba(60,80,180,0.55), rgba(30,24,80,0.75))", border: "1px solid rgba(223,185,110,0.55)" }}>
                ✨ A blessing of {b.kind}
              </button>
            ))}
          </motion.div>
        ) : (
          <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
            <p className="text-slate-300 text-[14px] leading-relaxed">
              A blessing appears when something meaningful happens on your path — a Sankalp fulfilled, a quiet streak of Karma, a truthful outcome shared. Keep walking.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {recent.length > 0 && !revealed && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Past blessings</div>
          <div className="space-y-2">
            {recent.map((b) => (
              <div key={b.id} className="rounded-xl bg-white/5 border border-white/5 px-4 py-2 text-slate-300 text-[12.5px] italic">
                “{b.message}”
              </div>
            ))}
          </div>
        </div>
      )}
    </GlassSheet>
  );
}
